import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, type ReactNode } from 'react';

import { ApiError } from '../api/client';
import { api } from '../api/endpoints';
import type { UserContext } from '../api/types';
import { meQueryKey } from '../app/queryClient';
import { AuthContext, type AuthContextValue } from './context';

async function fetchMe({ signal }: { signal: AbortSignal }): Promise<UserContext | null> {
  try {
    return await api.me(signal);
  } catch (error) {
    if (error instanceof ApiError && error.isUnauthorized) return null;
    throw error;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: meQueryKey, queryFn: fetchMe, staleTime: 5 * 60_000 });

  const login = useCallback(
    async (email: string, password: string) => {
      await api.login(email, password);
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== meQueryKey[0] });
      await queryClient.fetchQuery({ queryKey: meQueryKey, queryFn: fetchMe, staleTime: 0 });
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      // Erst den Benutzerkontext leeren (aktualisiert alle Beobachter), dann übrige Daten verwerfen.
      queryClient.setQueryData(meQueryKey, null);
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== meQueryKey[0] });
    }
  }, [queryClient]);

  const { data, error, isPending, isError, refetch } = query;
  const value = useMemo<AuthContextValue>(() => {
    let status: AuthContextValue['status'] = 'authenticated';
    if (isPending) status = 'loading';
    else if (isError) status = 'error';
    else if (!data) status = 'unauthenticated';
    return { status, me: data ?? null, error, login, logout, retry: () => void refetch() };
  }, [data, error, isPending, isError, refetch, login, logout]);

  return <AuthContext value={value}>{children}</AuthContext>;
}
